import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Upload, Download, Trash2, Search, Lock, Unlock, MoreVertical,
  FileText, Image, File, FileSpreadsheet, Film, Music, Archive, FolderOpen, Folder,
  Shield, HardDrive, Globe, UserCircle, Edit, Plus,
  Share2, Link, Copy, Mail, Clock, CheckCircle, ChevronRight, Home, FolderPlus
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_ICONS = {
  image: Image, pdf: FileText, document: FileText, spreadsheet: FileSpreadsheet,
  presentation: FileText, archive: Archive, video: Film, audio: Music, other: File,
};
const FILE_COLORS = {
  image: 'bg-pink-100 text-pink-600', pdf: 'bg-red-100 text-red-600',
  document: 'bg-blue-100 text-blue-600', spreadsheet: 'bg-emerald-100 text-emerald-600',
  presentation: 'bg-orange-100 text-orange-600', archive: 'bg-slate-100 text-slate-600',
  video: 'bg-purple-100 text-purple-600', audio: 'bg-amber-100 text-amber-600',
  other: 'bg-slate-100 text-slate-600',
};
const TYPE_FILTERS = ['all', 'image', 'pdf', 'document', 'spreadsheet', 'archive', 'video', 'audio', 'other'];

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const RepositoryPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('my-files');
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Folder navigation
  const [currentFolder, setCurrentFolder] = useState(null); // null = root
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [folderUnlocks, setFolderUnlocks] = useState(new Set()); // unlocked folder ids in this session

  // Dialogs
  const [showUpload, setShowUpload] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showFolderUnlock, setShowFolderUnlock] = useState(null);
  const [showEditFolder, setShowEditFolder] = useState(null);
  const [showPassword, setShowPassword] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [showShare, setShowShare] = useState(null);
  const [shareResult, setShareResult] = useState(null);
  const [sharePw, setSharePw] = useState('');
  const [shareEmails, setShareEmails] = useState('');
  const [shareExpires, setShareExpires] = useState('');
  const [existingShares, setExistingShares] = useState([]);
  const [passwordInput, setPasswordInput] = useState('');
  const [folderPwInput, setFolderPwInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [verifiedFiles, setVerifiedFiles] = useState(new Set());
  const fileInputRef = useRef(null);
  const multiFileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // Drag & drop / batch upload state
  const [dragOver, setDragOver] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // { total, done, failed, current }

  // Upload form
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadVis, setUploadVis] = useState('private');
  const [uploadPw, setUploadPw] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadBehalf, setUploadBehalf] = useState('self');

  // Team files (admin) view
  const [teamMembers, setTeamMembers] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState('all');

  // Create folder form
  const [folderName, setFolderName] = useState('');
  const [folderVis, setFolderVis] = useState('private');
  const [folderPw, setFolderPw] = useState('');
  const [folderDesc, setFolderDesc] = useState('');

  // Edit folder form
  const [editFolderName, setEditFolderName] = useState('');
  const [editFolderVis, setEditFolderVis] = useState('private');
  const [editFolderPw, setEditFolderPw] = useState('');
  const [editFolderDesc, setEditFolderDesc] = useState('');
  const [editFolderRemovePw, setEditFolderRemovePw] = useState(false);

  // Edit file form
  const [editDesc, setEditDesc] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editVis, setEditVis] = useState('');
  const [editNewPw, setEditNewPw] = useState('');
  const [editRemovePw, setEditRemovePw] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    setCurrentFolder(null);
    setBreadcrumbs([]);
    loadData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, ownerFilter]);

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/users`).then(res => setTeamMembers(res.data || [])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const visParam = activeTab === 'my-files' ? 'private' : 'public';

  const loadData = async (folderId) => {
    setLoading(true);
    try {
      if (folderId) {
        // Load folder contents
        const [contentsRes, statsRes] = await Promise.all([
          axios.get(`${API}/repository/folders/${folderId}/contents`),
          axios.get(`${API}/repository/stats`)
        ]);
        setFolders(contentsRes.data.subfolders || []);
        setFiles(contentsRes.data.files || []);
        setBreadcrumbs(contentsRes.data.breadcrumbs || []);
        setStats(statsRes.data);
      } else if (activeTab === 'team-files') {
        // Admin view: flat list of ALL team members' files (any folder, any visibility)
        const ownerParam = ownerFilter !== 'all' ? `?owner_id=${ownerFilter}` : '';
        const [filesRes, statsRes] = await Promise.all([
          axios.get(`${API}/repository/files${ownerParam}`),
          axios.get(`${API}/repository/stats`)
        ]);
        setFolders([]);
        setFiles(filesRes.data || []);
        setBreadcrumbs([]);
        setStats(statsRes.data);
      } else {
        // Root: load root folders + root files (folder_id = null)
        const [foldersRes, filesRes, statsRes] = await Promise.all([
          axios.get(`${API}/repository/folders?parent_id=null&visibility=${visParam}`),
          axios.get(`${API}/repository/files?folder_id=null&visibility=${visParam}`),
          axios.get(`${API}/repository/stats`)
        ]);
        setFolders(foldersRes.data || []);
        setFiles(filesRes.data || []);
        setBreadcrumbs([]);
        setStats(statsRes.data);
      }
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.detail?.includes('password')) {
        toast.error('Folder password required');
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load');
      }
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = async (folder) => {
    if (folder.has_password && !isAdmin && !folderUnlocks.has(folder.id) && folder.owner_id !== user?.id) {
      setShowFolderUnlock(folder);
      setFolderPwInput('');
      return;
    }
    setCurrentFolder(folder);
    loadData(folder.id);
  };

  const navigateToBreadcrumb = (folder) => {
    if (!folder) {
      setCurrentFolder(null);
      loadData(null);
    } else {
      setCurrentFolder(folder);
      loadData(folder.id);
    }
  };

  const handleVerifyFolderPassword = async () => {
    if (!showFolderUnlock || !folderPwInput) return;
    try {
      await axios.post(`${API}/repository/folders/${showFolderUnlock.id}/verify-password`, { password: folderPwInput });
      setFolderUnlocks(prev => new Set([...prev, showFolderUnlock.id]));
      toast.success('Folder unlocked');
      const folder = showFolderUnlock;
      setShowFolderUnlock(null);
      setCurrentFolder(folder);
      loadData(folder.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Incorrect password'); }
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) { toast.error('Folder name is required'); return; }
    try {
      const body = {
        name: folderName.trim(),
        parent_id: currentFolder?.id || null,
        visibility: folderVis,
        description: folderDesc || null,
      };
      if (folderPw && folderVis === 'public') body.password = folderPw;
      await axios.post(`${API}/repository/folders`, body);
      toast.success('Folder created');
      setShowCreateFolder(false);
      setFolderName(''); setFolderVis('private'); setFolderPw(''); setFolderDesc('');
      loadData(currentFolder?.id || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create folder'); }
  };

  const openEditFolder = (folder) => {
    setShowEditFolder(folder);
    setEditFolderName(folder.name);
    setEditFolderVis(folder.visibility);
    setEditFolderPw('');
    setEditFolderDesc(folder.description || '');
    setEditFolderRemovePw(false);
  };

  const handleSaveFolder = async () => {
    if (!showEditFolder) return;
    try {
      const body = {
        name: editFolderName,
        visibility: editFolderVis,
        description: editFolderDesc || null,
      };
      if (editFolderPw) body.password = editFolderPw;
      if (editFolderRemovePw) body.remove_password = true;
      await axios.put(`${API}/repository/folders/${showEditFolder.id}`, body);
      toast.success('Folder updated');
      setShowEditFolder(null);
      loadData(currentFolder?.id || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update folder'); }
  };

  const handleDeleteFolder = async (folder) => {
    const hasContent = (folder.file_count || 0) + (folder.subfolder_count || 0) > 0;
    const msg = hasContent
      ? `Folder "${folder.name}" contains ${folder.file_count || 0} files and ${folder.subfolder_count || 0} subfolders. Delete EVERYTHING permanently?`
      : `Delete folder "${folder.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/repository/folders/${folder.id}?recursive=${hasContent}`);
      toast.success('Folder deleted');
      loadData(currentFolder?.id || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete folder'); }
  };

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Select a file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      // If inside a folder, file inherits folder visibility automatically server-side
      formData.append('visibility', currentFolder ? currentFolder.visibility : uploadVis);
      const effectiveVis = currentFolder ? currentFolder.visibility : uploadVis;
      if (uploadPw && effectiveVis === 'public') formData.append('password', uploadPw);
      if (uploadDesc) formData.append('description', uploadDesc);
      if (uploadTags) formData.append('tags', uploadTags);
      if (currentFolder?.id) formData.append('folder_id', currentFolder.id);
      if (isAdmin && uploadBehalf && uploadBehalf !== 'self') formData.append('on_behalf_of', uploadBehalf);
      await axios.post(`${API}/repository/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(uploadBehalf !== 'self' ? 'File uploaded on behalf of team member' : 'File uploaded');
      setShowUpload(false);
      resetUploadForm();
      loadData(currentFolder?.id || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const resetUploadForm = () => {
    setUploadFile(null); setUploadVis(activeTab === 'my-files' ? 'private' : 'public'); setUploadPw('');
    setUploadDesc(''); setUploadTags(''); setUploadBehalf('self');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ---- Batch / drag-drop / folder upload helpers ----

  // Upload a single file to a given folder (or root)
  const uploadOneFile = async (fileBlob, folderId, visibility) => {
    const formData = new FormData();
    formData.append('file', fileBlob, fileBlob.name);
    formData.append('visibility', visibility);
    if (folderId) formData.append('folder_id', folderId);
    return axios.post(`${API}/repository/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  // Get-or-create a folder by name under a parent (caches by full path key)
  const ensureFolder = async (name, parentId, parentPathKey, visibility, cache) => {
    const key = `${parentPathKey}/${name}`;
    if (cache.has(key)) return cache.get(key);
    // Try to find it first
    try {
      const res = await axios.get(`${API}/repository/folders?parent_id=${parentId || 'null'}`);
      const existing = (res.data || []).find(f => f.name === name && (f.parent_id || null) === (parentId || null));
      if (existing) {
        cache.set(key, existing);
        return existing;
      }
    } catch { /* ignore */ }
    // Create
    const created = await axios.post(`${API}/repository/folders`, {
      name, parent_id: parentId || null, visibility,
    });
    cache.set(key, created.data);
    return created.data;
  };

  // Upload a list of File objects (each may have webkitRelativePath for folder structure)
  const uploadBatch = async (fileList) => {
    const filesArr = Array.from(fileList || []);
    if (filesArr.length === 0) return;

    const baseVisibility = currentFolder ? currentFolder.visibility : (activeTab === 'my-files' ? 'private' : 'public');
    const baseParentId = currentFolder?.id || null;
    const basePathKey = currentFolder ? `__base_${currentFolder.id}` : '__base_root';
    const folderCache = new Map();

    setBatchProgress({ total: filesArr.length, done: 0, failed: 0, current: filesArr[0]?.name || '' });

    let done = 0, failed = 0;
    for (const f of filesArr) {
      try {
        setBatchProgress(p => ({ ...p, current: f.webkitRelativePath || f.name }));
        // Determine subfolder path
        const rel = f.webkitRelativePath || '';
        const parts = rel ? rel.split('/').slice(0, -1) : []; // exclude filename
        let parentId = baseParentId;
        let pathKey = basePathKey;
        for (const part of parts) {
          if (!part) continue;
          const folder = await ensureFolder(part, parentId, pathKey, baseVisibility, folderCache);
          parentId = folder.id;
          pathKey = `${pathKey}/${part}`;
        }
        await uploadOneFile(f, parentId, baseVisibility);
        done += 1;
      } catch (e) {
        failed += 1;
        console.error('Upload failed for', f.name, e);
      } finally {
        setBatchProgress(p => p ? { ...p, done, failed } : null);
      }
    }
    if (failed === 0) toast.success(`Uploaded ${done} file${done === 1 ? '' : 's'}`);
    else toast.warning(`Uploaded ${done}, failed ${failed}`);
    setBatchProgress(null);
    loadData(currentFolder?.id || null);
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  // Recursively read a DataTransferItem entry (file or directory) and collect File objects with relative paths
  const traverseFileTree = (entry, pathPrefix, collected) => new Promise((resolve) => {
    if (entry.isFile) {
      entry.file((file) => {
        // Attach relative path
        try {
          Object.defineProperty(file, 'webkitRelativePath', { value: `${pathPrefix}${file.name}` });
        } catch { /* some browsers may not allow override; fall back to wrapping */ }
        collected.push(file);
        resolve();
      }, () => resolve());
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const readAll = () => {
        reader.readEntries(async (entries) => {
          if (!entries.length) { resolve(); return; }
          await Promise.all(entries.map(en => traverseFileTree(en, `${pathPrefix}${entry.name}/`, collected)));
          readAll();
        }, () => resolve());
      };
      readAll();
    } else {
      resolve();
    }
  });

  const onDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const items = e.dataTransfer?.items;
    const collected = [];
    if (items && items.length && items[0].webkitGetAsEntry) {
      const entries = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }
      for (const entry of entries) {
        await traverseFileTree(entry, '', collected);
      }
    } else if (e.dataTransfer?.files) {
      for (const f of e.dataTransfer.files) collected.push(f);
    }
    if (collected.length === 0) { toast.error('No files dropped'); return; }
    await uploadBatch(collected);
  };

  const onPickMultiFiles = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await uploadBatch(files);
    if (multiFileInputRef.current) multiFileInputRef.current.value = '';
  };

  const onPickFolder = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await uploadBatch(files);
    if (folderInputRef.current) folderInputRef.current.value = '';
  };


  const handleDownload = async (file, passwordOverride) => {
    if (file.has_password && !isAdmin && file.owner_id !== user?.id && !passwordOverride) {
      setShowPassword(file); setPasswordInput(''); return;
    }
    try {
      const pw = passwordOverride ? `?password=${encodeURIComponent(passwordOverride)}` : '';
      const res = await axios.get(`${API}/repository/files/${file.id}/download${pw}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = file.original_filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Download started');
      loadData(currentFolder?.id || null);
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.detail?.includes('Password')) {
        setShowPassword(file); setPasswordInput('');
      } else { toast.error(e.response?.data?.detail || 'Download failed'); }
    }
  };

  const handleVerifyPassword = async () => {
    if (!showPassword || !passwordInput) return;
    try {
      await axios.post(`${API}/repository/files/${showPassword.id}/verify-password`, { password: passwordInput });
      setVerifiedFiles(prev => new Set([...prev, showPassword.id]));
      toast.success('Password verified');
      const file = showPassword;
      const pw = passwordInput;
      setShowPassword(null);
      await handleDownload(file, pw);
    } catch (e) { toast.error(e.response?.data?.detail || 'Incorrect password'); }
  };

  const handleFolderDownload = async (folder, passwordOverride) => {
    try {
      const pw = passwordOverride ? `?password=${encodeURIComponent(passwordOverride)}` : '';
      toast.info(`Preparing "${folder.name}.zip"...`);
      const res = await axios.get(`${API}/repository/folders/${folder.id}/download${pw}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${folder.name}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Folder download started');
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ }
      }
      if (e.response?.status === 403 && detail?.includes('Password')) {
        const pw = window.prompt(`"${folder.name}" is password protected. Enter the folder password to download:`);
        if (pw) await handleFolderDownload(folder, pw);
      } else { toast.error(detail || 'Folder download failed'); }
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file permanently?')) return;
    try { await axios.delete(`${API}/repository/files/${fileId}`); toast.success('File deleted'); loadData(currentFolder?.id || null); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (file) => {
    setShowEdit(file);
    setEditDesc(file.description || '');
    setEditTags((file.tags || []).join(', '));
    setEditVis(file.visibility);
    setEditNewPw('');
    setEditRemovePw(false);
  };

  const handleEdit = async () => {
    if (!showEdit) return;
    try {
      const body = {
        description: editDesc || null,
        tags: editTags ? editTags.split(',').map(t => t.trim()).filter(Boolean) : [],
        visibility: editVis,
      };
      if (editNewPw) body.password = editNewPw;
      if (editRemovePw) body.remove_password = true;
      await axios.put(`${API}/repository/files/${showEdit.id}`, body);
      toast.success('File updated');
      setShowEdit(null);
      loadData(currentFolder?.id || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openShare = async (file) => {
    setShowShare(file); setShareResult(null); setSharePw(''); setShareEmails(''); setShareExpires('');
    try { const res = await axios.get(`${API}/repository/files/${file.id}/shares`); setExistingShares(res.data); } catch { setExistingShares([]); }
  };

  const handleCreateShare = async () => {
    if (!showShare) return;
    try {
      const body = { file_id: showShare.id, allow_anyone_with_link: true };
      if (sharePw) body.password = sharePw;
      if (shareEmails.trim()) body.shared_with_emails = shareEmails.split(',').map(e => e.trim()).filter(Boolean);
      if (shareExpires) body.expires_hours = parseInt(shareExpires);
      const res = await axios.post(`${API}/repository/files/${showShare.id}/share`, body);
      const baseUrl = window.location.origin;
      setShareResult({ ...res.data, link: `${baseUrl}/shared/${res.data.share_token}` });
      toast.success('Share link created');
      const sharesRes = await axios.get(`${API}/repository/files/${showShare.id}/shares`);
      setExistingShares(sharesRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create share link'); }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => toast.success('Link copied to clipboard')).catch(() => toast.error('Failed to copy'));
  };

  const handleRevokeShare = async (shareId) => {
    try { await axios.delete(`${API}/repository/shares/${shareId}`); toast.success('Share link revoked'); setExistingShares(prev => prev.filter(s => s.id !== shareId)); } catch { toast.error('Failed'); }
  };

  const filteredFiles = files.filter(f => {
    if (filterType !== 'all' && f.file_type !== filterType) return false;
    if (search) {
      const q = search.toLowerCase();
      return f.original_filename?.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q) ||
        f.owner_name?.toLowerCase().includes(q) ||
        (f.tags || []).some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  const filteredFolders = folders.filter(f => {
    if (search) {
      const q = search.toLowerCase();
      return f.name?.toLowerCase().includes(q) || f.description?.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="repository-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Repository</h1>
          <p className="text-muted-foreground mt-1">Folders & files with private/public, password & shareable link security</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => { setFolderName(''); setFolderVis(currentFolder?.visibility || (activeTab === 'my-files' ? 'private' : 'public')); setFolderPw(''); setFolderDesc(''); setShowCreateFolder(true); }} data-testid="new-folder-btn">
            <FolderPlus className="h-4 w-4 mr-2" /> New Folder
          </Button>
          <Button variant="outline" onClick={() => multiFileInputRef.current?.click()} data-testid="upload-multi-btn" disabled={!!batchProgress}>
            <Upload className="h-4 w-4 mr-2" /> Upload Files
          </Button>
          <Button variant="outline" onClick={() => folderInputRef.current?.click()} data-testid="upload-folder-btn" disabled={!!batchProgress}>
            <FolderPlus className="h-4 w-4 mr-2" /> Upload Folder
          </Button>
          <Button onClick={() => { resetUploadForm(); setShowUpload(true); }} data-testid="upload-file-btn">
            <Upload className="h-4 w-4 mr-2" /> Single Upload
          </Button>
          {/* Hidden inputs */}
          <input ref={multiFileInputRef} type="file" multiple className="hidden" onChange={onPickMultiFiles} data-testid="multi-file-input" />
          <input ref={folderInputRef} type="file" className="hidden" onChange={onPickFolder} webkitdirectory="" directory="" mozdirectory="" data-testid="folder-input" />
        </div>
      </div>

      {/* Batch progress */}
      {batchProgress && (
        <Card className="border-primary/40">
          <CardContent className="p-4 space-y-2" data-testid="batch-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium flex items-center gap-2">
                <Upload className="h-4 w-4 animate-pulse" />
                Uploading {batchProgress.done + batchProgress.failed}/{batchProgress.total}
              </span>
              <span className="text-muted-foreground text-xs truncate max-w-[60%]">{batchProgress.current}</span>
            </div>
            <div className="h-1.5 w-full bg-muted rounded overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${Math.round(((batchProgress.done + batchProgress.failed) / batchProgress.total) * 100)}%` }}
              />
            </div>
            {batchProgress.failed > 0 && <p className="text-xs text-red-600">{batchProgress.failed} file(s) failed</p>}
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center"><FolderOpen className="h-5 w-5 text-blue-600" /></div><div><p className="text-xs text-muted-foreground">My Files</p><p className="text-xl font-bold font-mono">{stats.my_files || 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Globe className="h-5 w-5 text-emerald-600" /></div><div><p className="text-xs text-muted-foreground">Public Files</p><p className="text-xl font-bold font-mono">{stats.public_files || 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center"><Lock className="h-5 w-5 text-amber-600" /></div><div><p className="text-xs text-muted-foreground">Protected</p><p className="text-xl font-bold font-mono">{stats.password_protected || 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center"><HardDrive className="h-5 w-5 text-purple-600" /></div><div><p className="text-xs text-muted-foreground">Storage</p><p className="text-xl font-bold font-mono">{stats.total_storage_formatted || '0 B'}</p></div></CardContent></Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList>
            <TabsTrigger value="my-files" data-testid="my-files-tab"><FolderOpen className="h-3 w-3 mr-1" /> My Files</TabsTrigger>
            <TabsTrigger value="public-files" data-testid="public-files-tab"><Globe className="h-3 w-3 mr-1" /> Public Files</TabsTrigger>
            {isAdmin && <TabsTrigger value="team-files" data-testid="team-files-tab"><UserCircle className="h-3 w-3 mr-1" /> Team Files</TabsTrigger>}
          </TabsList>
          <div className="flex items-center gap-3">
            {activeTab === 'team-files' && isAdmin && (
              <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                <SelectTrigger className="w-[200px]" data-testid="owner-filter-select"><SelectValue placeholder="All team members" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Team Members</SelectItem>
                  {teamMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search..." className="pl-9 w-64" value={search} onChange={e => setSearch(e.target.value)} data-testid="file-search" /></div>
            <Select value={filterType} onValueChange={setFilterType}><SelectTrigger className="w-[130px]"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>{TYPE_FILTERS.map(t => <SelectItem key={t} value={t}>{t === 'all' ? 'All Types' : t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>

        {/* Breadcrumbs (folder navigation tabs only) */}
        {activeTab !== 'team-files' && (
        <div className="mt-4 flex items-center gap-1 text-sm flex-wrap" data-testid="folder-breadcrumbs">
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => navigateToBreadcrumb(null)} data-testid="breadcrumb-root">
            <Home className="h-3.5 w-3.5 mr-1" /> {activeTab === 'my-files' ? 'My Files' : 'Public Files'}
          </Button>
          {breadcrumbs.map((b, idx) => (
            <span key={b.id} className="flex items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => navigateToBreadcrumb(b)} disabled={idx === breadcrumbs.length - 1} data-testid={`breadcrumb-${b.id}`}>
                {b.has_password && <Lock className="h-3 w-3 mr-1 text-amber-500" />}
                {b.name}
              </Button>
            </span>
          ))}
        </div>
        )}

        <TabsContent value="my-files" className="mt-3">
          <div
            onDragOver={onDragOver} onDragEnter={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
            className={`relative rounded-lg transition-colors ${dragOver ? 'ring-2 ring-primary ring-offset-2 bg-primary/5' : ''}`}
            data-testid="dropzone-my"
          >
            {dragOver && (
              <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none rounded-lg bg-primary/10 border-2 border-dashed border-primary">
                <div className="text-center">
                  <Upload className="h-10 w-10 mx-auto mb-2 text-primary" />
                  <p className="text-sm font-medium">Drop files or folders to upload {currentFolder ? `into "${currentFolder.name}"` : '(root)'}</p>
                </div>
              </div>
            )}
            {loading ? <div className="text-center py-12 text-muted-foreground animate-pulse">Loading...</div> : (
              <BrowserView
                folders={filteredFolders} files={filteredFiles}
                onOpenFolder={navigateToFolder} onEditFolder={openEditFolder} onDeleteFolder={handleDeleteFolder}
                onDownload={handleDownload} onDownloadFolder={handleFolderDownload} onDelete={handleDelete} onEdit={openEdit} onShare={openShare}
                isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} currentUserId={user?.id}
              />
            )}
          </div>
        </TabsContent>
        <TabsContent value="public-files" className="mt-3">
          <div
            onDragOver={onDragOver} onDragEnter={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
            className={`relative rounded-lg transition-colors ${dragOver ? 'ring-2 ring-primary ring-offset-2 bg-primary/5' : ''}`}
            data-testid="dropzone-public"
          >
            {dragOver && (
              <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none rounded-lg bg-primary/10 border-2 border-dashed border-primary">
                <div className="text-center">
                  <Upload className="h-10 w-10 mx-auto mb-2 text-primary" />
                  <p className="text-sm font-medium">Drop files or folders to upload {currentFolder ? `into "${currentFolder.name}"` : '(root)'}</p>
                </div>
              </div>
            )}
            {loading ? <div className="text-center py-12 text-muted-foreground animate-pulse">Loading...</div> : (
              <BrowserView
                folders={filteredFolders} files={filteredFiles}
                onOpenFolder={navigateToFolder} onEditFolder={openEditFolder} onDeleteFolder={handleDeleteFolder}
                onDownload={handleDownload} onDownloadFolder={handleFolderDownload} onDelete={handleDelete} onEdit={openEdit} onShare={openShare}
                isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} currentUserId={user?.id} showOwner
              />
            )}
          </div>
        </TabsContent>
        {isAdmin && (
          <TabsContent value="team-files" className="mt-3">
            <p className="text-xs text-muted-foreground mb-2" data-testid="team-files-hint">
              All files across the organisation (private and public, in every folder). Filter by team member to see, download, edit or manage their files.
            </p>
            {loading ? <div className="text-center py-12 text-muted-foreground animate-pulse">Loading...</div> : (
              <BrowserView
                folders={[]} files={filteredFiles}
                onOpenFolder={() => {}} onEditFolder={() => {}} onDeleteFolder={() => {}}
                onDownload={handleDownload} onDownloadFolder={handleFolderDownload} onDelete={handleDelete} onEdit={openEdit} onShare={openShare}
                isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} currentUserId={user?.id} showOwner
              />
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Create Folder Dialog */}
      <Dialog open={showCreateFolder} onOpenChange={setShowCreateFolder}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Folder {currentFolder ? `inside "${currentFolder.name}"` : '(root)'}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Folder Name *</Label><Input value={folderName} onChange={e => setFolderName(e.target.value)} placeholder="e.g. 2026 Contracts" data-testid="folder-name-input" autoFocus /></div>
            <div className="space-y-1"><Label>Description</Label><Input value={folderDesc} onChange={e => setFolderDesc(e.target.value)} placeholder="Optional" /></div>
            <div className="space-y-1"><Label>Visibility</Label>
              <Select value={folderVis} onValueChange={setFolderVis}><SelectTrigger data-testid="folder-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private (only you and super admin)</SelectItem>
                  <SelectItem value="public">Public (everyone in org)</SelectItem>
                </SelectContent></Select>
              {currentFolder && <p className="text-xs text-amber-600 mt-1">Files uploaded into this folder will inherit its visibility ({currentFolder.visibility}).</p>}
            </div>
            {folderVis === 'public' && (
              <div className="space-y-1"><Label>Folder Password (optional)</Label>
                <Input type="password" value={folderPw} onChange={e => setFolderPw(e.target.value)} placeholder="Leave empty for no password" data-testid="folder-password-input" />
                <p className="text-xs text-muted-foreground">Users will need this password to enter the folder. Admins can override.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateFolder(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} data-testid="create-folder-submit"><Plus className="h-4 w-4 mr-1" /> Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Unlock Dialog */}
      <Dialog open={!!showFolderUnlock} onOpenChange={() => setShowFolderUnlock(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5" /> Folder Locked</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <p className="text-sm text-muted-foreground">This folder is password protected:</p>
            <p className="text-sm font-medium">{showFolderUnlock?.name}</p>
            <Input type="password" placeholder="Enter folder password" value={folderPwInput} onChange={e => setFolderPwInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleVerifyFolderPassword()} data-testid="folder-unlock-input" autoFocus />
            {isAdmin && <p className="text-xs text-amber-600 flex items-center gap-1"><Shield className="h-3 w-3" /> As {user?.role}, you can bypass folder password</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFolderUnlock(null)}>Cancel</Button>
            <Button onClick={handleVerifyFolderPassword} data-testid="folder-unlock-submit">Unlock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog open={!!showEditFolder} onOpenChange={() => setShowEditFolder(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Folder</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Name</Label><Input value={editFolderName} onChange={e => setEditFolderName(e.target.value)} /></div>
            <div className="space-y-1"><Label>Description</Label><Input value={editFolderDesc} onChange={e => setEditFolderDesc(e.target.value)} /></div>
            <div className="space-y-1"><Label>Visibility</Label>
              <Select value={editFolderVis} onValueChange={setEditFolderVis}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                </SelectContent></Select></div>
            {editFolderVis === 'public' && (
              <>
                <div className="space-y-1"><Label>Set / Change Password</Label><Input type="password" value={editFolderPw} onChange={e => { setEditFolderPw(e.target.value); setEditFolderRemovePw(false); }} placeholder="Leave empty to keep current" /></div>
                {showEditFolder?.has_password && (
                  <Button variant="outline" size="sm" className="text-xs w-fit" onClick={() => { setEditFolderRemovePw(true); setEditFolderPw(''); }}>
                    <Unlock className="h-3 w-3 mr-1" /> Remove Password
                  </Button>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditFolder(null)}>Cancel</Button>
            <Button onClick={handleSaveFolder} data-testid="save-folder-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent><DialogHeader><DialogTitle>Upload File {currentFolder ? `into "${currentFolder.name}"` : '(root)'}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-3">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors" onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium">{uploadFile ? uploadFile.name : 'Click to select file'}</p>
              {uploadFile && <p className="text-xs text-muted-foreground mt-1">{formatSize(uploadFile.size)}</p>}
              <input ref={fileInputRef} type="file" className="hidden" onChange={e => setUploadFile(e.target.files?.[0] || null)} data-testid="file-input" />
            </div>
            {isAdmin && (
              <div className="space-y-1"><Label>Upload on behalf of</Label>
                <Select value={uploadBehalf} onValueChange={setUploadBehalf}>
                  <SelectTrigger data-testid="upload-behalf-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself</SelectItem>
                    {teamMembers.filter(m => m.id !== user?.id).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {uploadBehalf !== 'self' && <p className="text-xs text-amber-600">The selected team member will be the file owner. This action is logged in the audit trail.</p>}
              </div>
            )}
            {currentFolder ? (
              <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                <Shield className="h-3.5 w-3.5 mt-0.5" />
                <span>File will inherit the folder's <b>{currentFolder.visibility}</b> visibility{currentFolder.has_password ? ' and password protection' : ''}.</span>
              </div>
            ) : (
              <div className="space-y-1"><Label>Visibility</Label>
                <Select value={uploadVis} onValueChange={setUploadVis}><SelectTrigger data-testid="visibility-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="private">Private (My Files)</SelectItem><SelectItem value="public">Public (Shared)</SelectItem></SelectContent></Select></div>
            )}
            {(currentFolder ? currentFolder.visibility : uploadVis) === 'public' && (
              <div className="space-y-1"><Label>File Password (optional)</Label><Input type="password" placeholder="Leave empty for no password" value={uploadPw} onChange={e => setUploadPw(e.target.value)} data-testid="upload-password" /></div>
            )}
            <div className="space-y-1"><Label>Description</Label><Input value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} placeholder="Optional description" /></div>
            <div className="space-y-1"><Label>Tags</Label><Input value={uploadTags} onChange={e => setUploadTags(e.target.value)} placeholder="tag1, tag2" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button><Button onClick={handleUpload} disabled={!uploadFile || uploading} data-testid="upload-submit">{uploading ? 'Uploading...' : 'Upload'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Dialog (for files) */}
      <Dialog open={!!showPassword} onOpenChange={() => setShowPassword(null)}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5" /> Password Required</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <p className="text-sm text-muted-foreground">This file is password protected. Enter the password to access:</p>
            <p className="text-sm font-medium">{showPassword?.original_filename}</p>
            <Input type="password" placeholder="Enter password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleVerifyPassword()} data-testid="access-password-input" autoFocus />
            {isAdmin && <p className="text-xs text-amber-600 flex items-center gap-1"><Shield className="h-3 w-3" /> As {user?.role}, you can bypass password protection</p>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowPassword(null)}>Cancel</Button><Button onClick={handleVerifyPassword} data-testid="verify-password-btn">Verify & Download</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit File Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent><DialogHeader><DialogTitle>Edit File</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <p className="text-sm font-medium">{showEdit?.original_filename}</p>
            <div className="space-y-1"><Label>Description</Label><Input value={editDesc} onChange={e => setEditDesc(e.target.value)} /></div>
            <div className="space-y-1"><Label>Tags</Label><Input value={editTags} onChange={e => setEditTags(e.target.value)} placeholder="tag1, tag2" /></div>
            <div className="space-y-1"><Label>Visibility</Label>
              <Select value={editVis} onValueChange={setEditVis}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="private">Private</SelectItem><SelectItem value="public">Public</SelectItem></SelectContent></Select></div>
            {editVis === 'public' && (
              <>
                <div className="space-y-1"><Label>Set New Password</Label><Input type="password" value={editNewPw} onChange={e => { setEditNewPw(e.target.value); setEditRemovePw(false); }} placeholder="Leave empty to keep current" /></div>
                {showEdit?.has_password && <Button variant="outline" size="sm" className="text-xs w-fit" onClick={() => { setEditRemovePw(true); setEditNewPw(''); }}><Unlock className="h-3 w-3 mr-1" /> Remove Password</Button>}
              </>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowEdit(null)}>Cancel</Button><Button onClick={handleEdit} data-testid="save-edit-btn">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={!!showShare} onOpenChange={() => setShowShare(null)}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="h-5 w-5" /> Share File</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <p className="text-sm font-medium">{showShare?.original_filename}</p>
            {!shareResult && (
              <div className="space-y-3 border rounded-lg p-4">
                <h4 className="text-sm font-semibold">Create Share Link</h4>
                <div className="space-y-1"><Label>Password Protection (optional)</Label><Input type="password" placeholder="Leave empty for no password" value={sharePw} onChange={e => setSharePw(e.target.value)} data-testid="share-password" /></div>
                <div className="space-y-1"><Label>Share with Emails (optional)</Label><Input placeholder="email1@example.com, email2@example.com" value={shareEmails} onChange={e => setShareEmails(e.target.value)} data-testid="share-emails" /><p className="text-xs text-muted-foreground">Comma-separated. Users will be notified.</p></div>
                <div className="space-y-1"><Label>Expires After (optional)</Label>
                  <Select value={shareExpires} onValueChange={setShareExpires}><SelectTrigger><SelectValue placeholder="Never expires" /></SelectTrigger>
                    <SelectContent><SelectItem value="">Never</SelectItem><SelectItem value="1">1 hour</SelectItem><SelectItem value="24">24 hours</SelectItem><SelectItem value="72">3 days</SelectItem><SelectItem value="168">7 days</SelectItem><SelectItem value="720">30 days</SelectItem></SelectContent></Select></div>
                <Button onClick={handleCreateShare} className="w-full" data-testid="create-share-btn"><Link className="h-4 w-4 mr-2" /> Generate Share Link</Button>
              </div>
            )}
            {shareResult && (
              <div className="space-y-3 border border-emerald-200 bg-emerald-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-emerald-700"><CheckCircle className="h-4 w-4" /><span className="text-sm font-semibold">Share Link Created</span></div>
                <div className="flex items-center gap-2">
                  <Input value={shareResult.link} readOnly className="font-mono text-xs" data-testid="share-link-input" />
                  <Button size="sm" variant="outline" onClick={() => handleCopyLink(shareResult.link)} data-testid="copy-link-btn"><Copy className="h-4 w-4" /></Button>
                </div>
                {shareResult.has_password && <p className="text-xs text-amber-700 flex items-center gap-1"><Lock className="h-3 w-3" /> Password protected — share the password separately</p>}
                {shareResult.shared_with_emails?.length > 0 && <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> Notified: {shareResult.shared_with_emails.join(', ')}</p>}
                <Button variant="outline" size="sm" className="w-full" onClick={() => setShareResult(null)}>Create Another Link</Button>
              </div>
            )}
            {existingShares.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Active Share Links ({existingShares.length})</h4>
                <ScrollArea className="max-h-[200px]">
                  {existingShares.map(s => (
                    <div key={s.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg mb-2 text-xs">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{s.share_token?.slice(0, 12)}...</span>
                          {s.has_password && <Lock className="h-3 w-3 text-amber-500" />}
                          <Badge variant="outline" className="text-[10px]">{s.access_count} views</Badge>
                        </div>
                        {s.shared_with_emails?.length > 0 && <p className="text-muted-foreground"><Mail className="h-3 w-3 inline mr-1" />{s.shared_with_emails.join(', ')}</p>}
                        {s.expires_at && <p className="text-muted-foreground"><Clock className="h-3 w-3 inline mr-1" />Expires: {new Date(s.expires_at).toLocaleString()}</p>}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleCopyLink(`${window.location.origin}/shared/${s.share_token}`)}><Copy className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => handleRevokeShare(s.id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowShare(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Combined Folder + File browser
const BrowserView = ({ folders, files, onOpenFolder, onEditFolder, onDeleteFolder, onDownload, onDownloadFolder, onDelete, onEdit, onShare, isAdmin, isSuperAdmin, currentUserId, showOwner }) => {
  if (folders.length === 0 && files.length === 0) {
    return (
      <Card><CardContent className="p-12 text-center text-muted-foreground">
        <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p>This folder is empty</p>
        <p className="text-xs mt-1">Use "New Folder" or "Upload File" to add content here.</p>
      </CardContent></Card>
    );
  }
  return (
    <Card><div data-testid="browser-list">
      <div className="divide-y">
        {/* Folders first */}
        {folders.map(folder => {
          const canEdit = folder.owner_id === currentUserId || isSuperAdmin;
          return (
            <div key={folder.id} className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors" data-testid={`folder-row-${folder.id}`}>
              <button onClick={() => onOpenFolder(folder)} className="h-10 w-10 rounded-lg bg-yellow-100 text-yellow-700 flex items-center justify-center shrink-0" data-testid={`open-folder-${folder.id}`}>
                {folder.has_password ? <Lock className="h-5 w-5" /> : <Folder className="h-5 w-5" />}
              </button>
              <button onClick={() => onOpenFolder(folder)} className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm truncate">{folder.name}</p>
                  {folder.visibility === 'private' && <Badge variant="outline" className="text-[10px] shrink-0">Private</Badge>}
                  {folder.visibility === 'public' && <Badge className="text-[10px] shrink-0 bg-emerald-100 text-emerald-700">Public</Badge>}
                  {folder.has_password && <Badge className="text-[10px] shrink-0 bg-amber-100 text-amber-700"><Lock className="h-2.5 w-2.5 mr-0.5" />Locked</Badge>}
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                  <span>Folder</span>
                  <span>{folder.subfolder_count || 0} subfolders</span>
                  <span>{folder.file_count || 0} files</span>
                  {showOwner && <span className="flex items-center gap-1"><UserCircle className="h-3 w-3" />{folder.owner_name}</span>}
                  <span>{new Date(folder.created_at).toLocaleDateString()}</span>
                </div>
                {folder.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{folder.description}</p>}
              </button>
              <div className="flex items-center gap-1 shrink-0">
                <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`folder-menu-${folder.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onOpenFolder(folder)}><FolderOpen className="h-4 w-4 mr-2" />Open</DropdownMenuItem>
                    {(folder.visibility === 'public' || folder.owner_id === currentUserId || isSuperAdmin) && (
                      <DropdownMenuItem onClick={() => onDownloadFolder(folder)} data-testid={`download-folder-${folder.id}`}>
                        <Download className="h-4 w-4 mr-2" />Download as ZIP
                      </DropdownMenuItem>
                    )}
                    {canEdit && <DropdownMenuItem onClick={() => onEditFolder(folder)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>}
                    {canEdit && <DropdownMenuItem className="text-red-600" onClick={() => onDeleteFolder(folder)} data-testid={`delete-folder-${folder.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>}
                  </DropdownMenuContent></DropdownMenu>
              </div>
            </div>
          );
        })}
        {/* Files */}
        {files.map(file => {
          const IconComp = FILE_ICONS[file.file_type] || File;
          const colorClass = FILE_COLORS[file.file_type] || FILE_COLORS.other;
          const isOwner = file.owner_id === currentUserId;
          const canEdit = isOwner || isSuperAdmin;
          return (
            <div key={file.id} className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors" data-testid={`file-row-${file.id}`}>
              <div className={`h-10 w-10 rounded-lg ${colorClass} flex items-center justify-center shrink-0`}>
                <IconComp className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm truncate">{file.original_filename}</p>
                  {file.has_password && <Lock className="h-3 w-3 text-amber-500 shrink-0" />}
                  {file.visibility === 'private' && <Badge variant="outline" className="text-[10px] shrink-0">Private</Badge>}
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                  <span>{formatSize(file.file_size)}</span>
                  <span>{file.file_type}</span>
                  {showOwner && <span className="flex items-center gap-1"><UserCircle className="h-3 w-3" />{file.owner_name}</span>}
                  <span>{new Date(file.created_at).toLocaleDateString()}</span>
                  {file.download_count > 0 && <span>{file.download_count} downloads</span>}
                </div>
                {file.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{file.description}</p>}
                {file.tags?.length > 0 && (
                  <div className="flex gap-1 mt-1">{file.tags.map(t => <Badge key={t} variant="outline" className="text-[10px] px-1.5">{t}</Badge>)}</div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onDownload(file)} data-testid={`download-${file.id}`}><Download className="h-4 w-4" /></Button>
                <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`file-menu-${file.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onDownload(file)}><Download className="h-4 w-4 mr-2" />Download</DropdownMenuItem>
                    {canEdit && <DropdownMenuItem onClick={() => onEdit(file)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>}
                    {canEdit && <DropdownMenuItem onClick={() => onShare(file)}><Share2 className="h-4 w-4 mr-2" />Share Link</DropdownMenuItem>}
                    {canEdit && <DropdownMenuItem className="text-red-600" onClick={() => onDelete(file.id)} data-testid={`delete-file-${file.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>}
                    {isAdmin && file.has_password && <DropdownMenuItem className="text-amber-600"><Shield className="h-4 w-4 mr-2" />Admin Override</DropdownMenuItem>}
                  </DropdownMenuContent></DropdownMenu>
              </div>
            </div>
          );
        })}
      </div>
    </div></Card>
  );
};

export default RepositoryPage;
